import {
  Button,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { useAuth } from '@/domains/auth';
import { oversightApi } from '../../api/oversight-api';
import type { IStaffNote } from '../../api/oversight-contracts';
import type { IAdminParticipantDetail } from '../../api/participant-contracts';
import { LoadError, Pager, PastTheEnd, Section } from '../../components/admin-page';
import { NoteComposer, NoteList } from '../../components/staff-notes';
import { useClientPage } from './client-page';

/**
 * Staff's notes on the household — every member's whole history, written from any of their
 * responses or from here — and a box that writes a note attached to no response: what is known
 * about a person or a firm that no one cycle's response is the place for. Only its author can
 * edit or delete a note (SR-222), here as on a response's notes pane, and each re-reads the
 * hub.
 *
 * The list is the hub document's `notes`, newest first as survey-api sends them, paged here; each
 * is signed with its author's address and, where the household has more than one member, marked
 * with the member it is on (R11). The write stays `oversight-api.ts`'s, beside the notes route
 * it posts to, and re-reads the **hub** — the one read the page has.
 *
 * **A note is written on one participant**, because `staff_note` hangs off one: the one opened,
 * unless the household has more than one and another is picked beside the button. The pick is
 * a select rather than a composer per member — one box, one history, and the member a
 * choice made once, where the button is.
 */
export function NotesSection({
  members,
  notes,
  anchor,
  reread
}: {
  /** The household, the opened participant first (`hubMembers`). */
  members: IAdminParticipantDetail[];
  anchor: string;
  notes: IStaffNote[];
  reread: () => Promise<boolean>;
}) {
  const { rows, setPage, more, pager } = useClientPage(notes);
  const { user } = useAuth();
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  const [writeOn, setWriteOn] = React.useState(members[0].participantId);
  // The box is behind "New note" (SR-407), Salesforce's related list's New: an open box at the
  // head of every record was the section's largest thing, for a write made now and then.
  const [composing, setComposing] = React.useState(false);
  const multi = members.length > 1;
  const names = React.useMemo(
    () => new Map(members.map((member) => [member.participantId, member.displayName])),
    [members]
  );
  // A member a re-read no longer carries (unlinked in the meantime) is not written on: the
  // choice falls back to the participant opened, which is always there.
  const target = names.has(writeOn) ? writeOn : members[0].participantId;

  const write = async (body: string) => {
    setRefused(null);
    await oversightApi.writeParticipantNote(target, body);
    // The new note is the newest, and the list is newest first, so it is on page 1 — which is
    // where the page turns, whichever page was open, as the re-read brings it.
    setPage(1);
    setComposing(false);
    await reread();
  };

  return (
    <Section
      title="Notes"
      anchor={anchor}
      description={
        'Internal to staff — only its author can edit or delete it. A note written here is ' +
        'attached to no response; one written from a response links to it.'
      }
    >
      <CardContent className="grid gap-4">
        {refused && <LoadError message={refused} />}
        {!composing ? (
          <div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-new-note
              onClick={() => setComposing(true)}
            >
              New note
            </Button>
          </div>
        ) : (
          <NoteComposer
            autoFocus
            placeholder="What a colleague opening this participant next should know"
            write={write}
            onRefused={setRefused}
            onCancel={() => setComposing(false)}
            beside={
              multi && (
                <Select value={target} onValueChange={setWriteOn}>
                  <SelectTrigger className="w-auto max-w-full" aria-label="Write the note on">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.participantId} value={member.participantId}>
                        {member.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            }
          />
        )}
        {rows.length === 0 && pager.total > 0 ? (
          <p className="py-8 text-center text-muted-foreground">
            <PastTheEnd {...pager} counted={['note', 'notes']} onPage={setPage} />
          </p>
        ) : (
          <NoteList
            notes={rows}
            members={multi ? names : undefined}
            viewer={user?.accountId ?? ''}
            onChanged={reread}
          />
        )}
        <Pager {...pager} onPage={setPage} quiet onMore={more} />
      </CardContent>
    </Section>
  );
}
